/**
 * Which handles of a grid are drawn when some of them overlap on screen.
 */

import type { IHandle } from "./handles/IHandle";
import type { Point2D, Point3D } from "./types";
import { HANDLE_PRIORITY, HANDLE_OVERLAP_THRESHOLD } from "./types";

/**
 * Higher priority handles are always shown; a lower priority handle is hidden
 * when it overlaps one already kept.
 *
 * @param worldToScreen projection of a handle position to screen pixels
 * @returns the handles to draw
 */
export function selectVisibleHandles(
  handles: IHandle[],
  worldToScreen: (point: Point3D) => Point2D,
): Set<IHandle> {
  const visibleHandles = new Set<IHandle>();

  const screenPositions = new Map<IHandle, Point2D>();
  for (const handle of handles) {
    screenPositions.set(handle, worldToScreen(handle.worldPosition));
  }

  // Highest priority first
  const sortedHandles = [...handles].sort(
    (a, b) => (HANDLE_PRIORITY[b.type] ?? 0) - (HANDLE_PRIORITY[a.type] ?? 0),
  );

  for (const handle of sortedHandles) {
    const screenPos = screenPositions.get(handle)!;
    let isOverlapping = false;

    for (const visibleHandle of visibleHandles) {
      const visibleScreenPos = screenPositions.get(visibleHandle)!;
      const distance = Math.hypot(
        screenPos.x - visibleScreenPos.x,
        screenPos.y - visibleScreenPos.y,
      );
      // Sum of both radii plus a margin
      const minDistance =
        handle.drawOptions.radius +
        visibleHandle.drawOptions.radius +
        HANDLE_OVERLAP_THRESHOLD;

      if (distance < minDistance) {
        isOverlapping = true;
        break;
      }
    }

    if (!isOverlapping) visibleHandles.add(handle);
  }

  return visibleHandles;
}
